export const CONSENT_STORAGE_KEY = 'odd-cookie-consent'
export const CONSENT_VERSION = 1
export const CONSENT_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000

export interface ConsentPreferences {
  necessary: true
  analytics: boolean
  media: boolean
}

export interface ConsentRecord extends ConsentPreferences {
  version: number
  decidedAt: string
}

export const OPTIONAL_CONSENT_DEFAULTS: Pick<ConsentPreferences, 'analytics' | 'media'> = {
  analytics: false,
  media: false,
}

export function createConsentRecord(
  preferences: Pick<ConsentPreferences, 'analytics' | 'media'>,
  decidedAt = new Date(),
): ConsentRecord {
  return {
    version: CONSENT_VERSION,
    necessary: true,
    analytics: preferences.analytics,
    media: preferences.media,
    decidedAt: decidedAt.toISOString(),
  }
}

export function parseConsentRecord(
  raw: string | null,
  now = Date.now(),
): ConsentRecord | null {
  if (!raw) return null

  try {
    const value = JSON.parse(raw) as Partial<ConsentRecord>
    const decidedAt = typeof value.decidedAt === 'string' ? Date.parse(value.decidedAt) : NaN
    if (
      value.version !== CONSENT_VERSION ||
      value.necessary !== true ||
      typeof value.analytics !== 'boolean' ||
      typeof value.media !== 'boolean' ||
      !Number.isFinite(decidedAt) ||
      decidedAt > now ||
      now - decidedAt > CONSENT_MAX_AGE_MS
    ) {
      return null
    }

    return value as ConsentRecord
  } catch {
    return null
  }
}

export function readConsentRecord(): ConsentRecord | null {
  if (typeof window === 'undefined') return null
  try {
    return parseConsentRecord(window.localStorage.getItem(CONSENT_STORAGE_KEY))
  } catch {
    return null
  }
}

export function writeConsentRecord(record: ConsentRecord): void {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record))
  } catch {
    // Consent still applies for this page load when persistent storage is unavailable.
  }
}

export function clearOptionalTrackingStorage(): void {
  if (typeof window === 'undefined') return

  const cookieNames = document.cookie
    .split(';')
    .map((part) => part.split('=', 1)[0]?.trim() ?? '')
    .filter((name) => name.startsWith('_ga'))
  const hostnameParts = window.location.hostname.split('.')
  const rootDomain = hostnameParts.length > 2 ? hostnameParts.slice(-2).join('.') : window.location.hostname
  const domains = new Set(['', window.location.hostname, `.${window.location.hostname}`, rootDomain, `.${rootDomain}`])

  for (const name of cookieNames) {
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ''}; SameSite=Lax`
    }
  }

  try {
    for (const key of Object.keys(window.localStorage)) {
      if (key.startsWith('@firebase/performance/')) {
        window.localStorage.removeItem(key)
      }
    }
  } catch {
    // Some privacy modes block access to localStorage entirely.
  }
}
